/** The notification centre's model: unread counts, the Needs you filter, day groups, labels. Pure. */
import type { EscalationView, Notification, Urgency } from '@orgus/protocol';
import type { Tone } from '@orgus/ui';

export type CentreTab = 'all' | 'needs';

export const URGENCY_VIEW: Record<Urgency, { tone: Tone; label: string }> = {
  critical: { tone: 'failed', label: 'Critical' },
  high: { tone: 'waiting', label: 'High' },
  normal: { tone: 'neutral', label: 'Normal' },
  low: { tone: 'idle', label: 'Low' },
};

const KIND_LABEL: Record<string, string> = {
  'approval.pending': 'Approval',
  'session.waiting': 'Session waiting',
  'session.ended': 'Session finished',
  'session.unmetered': 'Not metered',
  'machine.offline': 'Machine',
  'pipeline.failed': 'Pipeline',
  'budget.alert': 'Budget',
  escalation: 'Escalated',
  digest: 'Daily summary',
  batch: 'Updates',
  test: 'Test',
};

/** A short human label for what raised a notification, or null. */
export function kindLabel(n: Pick<Notification, 'kind' | 'ref'>): string | null {
  if (n.kind && KIND_LABEL[n.kind]) return KIND_LABEL[n.kind]!;
  if (n.ref?.type === 'approval') return 'Approval';
  if (n.ref?.type === 'digest') return 'Daily summary';
  return null;
}

export const escalationKey = (n: Pick<Notification, 'ref'>) => (n.ref ? `${n.ref.type}:${n.ref.id}` : null);

/**
 * What the console knows now about the things notifications point at. A field is null when
 * the console cannot tell (that module is not running, or this is demo data).
 */
export interface LiveRefs {
  /** Ids of approvals still pending (state.ext.approvals). */
  pendingApprovals: ReadonlySet<string> | null;
  /** Session id to its status, for sessions the hub still lists. */
  sessions: ReadonlyMap<string, string> | null;
  /** Machine id to whether it is online. */
  machines: ReadonlyMap<string, boolean> | null;
}

/**
 * Is the item a notification points at still open? An approval still pending, a session still
 * waiting (or, for an unmetered alert, still running), a machine still offline. Null when
 * there is no item or the console cannot tell.
 */
export function itemOpen(n: Pick<Notification, 'ref' | 'kind'>, live: LiveRefs | undefined): boolean | null {
  const r = n.ref;
  if (!r || !live) return null;
  if (r.type === 'approval') return live.pendingApprovals ? live.pendingApprovals.has(r.id) : null;
  if (r.type === 'session') {
    if (!live.sessions) return null;
    const st = live.sessions.get(r.id);
    if (st === undefined) return false;
    return n.kind === 'session.unmetered' ? st !== 'ended' : st === 'waiting';
  }
  if (r.type === 'machine') {
    if (!live.machines) return null;
    const on = live.machines.get(r.id);
    return on === undefined ? false : !on;
  }
  return null;
}

/** Needs you: an item still escalating, or high or critical and not answered or resolved since. */
export function needsYou(n: Notification, open: ReadonlySet<string>, live?: LiveRefs): boolean {
  const k = escalationKey(n);
  if (k !== null && open.has(k)) return true;
  if (n.urgency !== 'high' && n.urgency !== 'critical') return false;
  return itemOpen(n, live) !== false;
}

/** A badge for a row whose item is closed now: 'Answered' for an approval, else 'Resolved'. Null while open or unknown. */
export function closedLabel(n: Pick<Notification, 'ref' | 'kind'>, live: LiveRefs | undefined): string | null {
  if (itemOpen(n, live) !== false) return null;
  return n.ref?.type === 'approval' ? 'Answered' : 'Resolved';
}

export function openEscalations(list: EscalationView[] | undefined): Map<string, EscalationView> {
  return new Map((list ?? []).map((e) => [e.key, e]));
}

/**
 * The item a notification points at is gone from the hub: a machine removed or a session no
 * longer listed. Such a row stays in the list, marked resolved, but it is no news to count (a
 * probe run that removes its machine leaves no inflated bell behind, R-FN-8).
 */
export function refGone(n: Pick<Notification, 'ref'>, live: LiveRefs | undefined): boolean {
  const r = n.ref;
  if (!r || !live) return false;
  if (r.type === 'session') return !!live.sessions && !live.sessions.has(r.id);
  if (r.type === 'machine') return !!live.machines && !live.machines.has(r.id);
  return false;
}

export function unreadCount(recent: Notification[], lastSeenAt: number, live?: LiveRefs): number {
  return recent.filter((n) => n.at > lastSeenAt && !refGone(n, live)).length;
}

/** The most urgent unread notification's urgency, for the bell's colour. */
export function topUnread(recent: Notification[], lastSeenAt: number): Urgency | null {
  let top: Urgency | null = null;
  const rank: Record<Urgency, number> = { low: 0, normal: 1, high: 2, critical: 3 };
  for (const n of recent) if (n.at > lastSeenAt && (top === null || rank[n.urgency] > rank[top])) top = n.urgency;
  return top;
}

/**
 * One row per thing: an escalation repeats its item, so only the newest notification for a
 * ref is shown (approvals, sessions, machines), and older ones fold into it.
 */
export function collapse(recent: Notification[]): Notification[] {
  const seen = new Set<string>();
  const out: Notification[] = [];
  for (const n of recent) {
    const k = n.ref && n.ref.type !== 'digest' ? `${n.ref.type}:${n.ref.id}` : null;
    if (k && seen.has(k)) continue;
    if (k) seen.add(k);
    out.push(n);
  }
  return out;
}

export function filterCentre(recent: Notification[], tab: CentreTab, open: ReadonlySet<string>, live?: LiveRefs): Notification[] {
  const list = collapse(recent);
  return tab === 'needs' ? list.filter((n) => needsYou(n, open, live)) : list;
}

export interface DayGroup {
  label: string;
  items: Notification[];
}

/** Today, Yesterday, Earlier, in the browser's local time. */
export function groupByDay(list: Notification[], now: number): DayGroup[] {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const today = start.getTime();
  const yesterday = today - 24 * 3600_000;
  const groups: DayGroup[] = [
    { label: 'Today', items: [] },
    { label: 'Yesterday', items: [] },
    { label: 'Earlier', items: [] },
  ];
  for (const n of list) groups[n.at >= today ? 0 : n.at >= yesterday ? 1 : 2]!.items.push(n);
  return groups.filter((g) => g.items.length);
}

/** Where the "last seen" mark lives when the hub cannot keep it (demo mode, an older hub). */
export const SEEN_KEY = 'orgus.notify.lastSeenAt';

/** A row in the centre: a notification, and the identical ones right after it that fold into it. */
export interface CentreRow<N extends Pick<Notification, 'id' | 'title' | 'actions' | 'ref'> = Notification> {
  n: N;
  /** Older notifications with the same title, folded under the newest ("and 3 more"). */
  more: N[];
}

/**
 * Folds notifications with the same title into one row under the newest (four "Landing queue
 * failed in studio" read as one, "+3"), wherever they fall in the list. Anything a person can act
 * on here (actions, an approval) stays on its own row, so a decision is never hidden in a fold.
 */
export function foldRepeats<N extends Pick<Notification, 'id' | 'title' | 'actions' | 'ref'>>(items: readonly N[]): CentreRow<N>[] {
  const out: CentreRow<N>[] = [];
  const actionable = (n: N) => (n.actions?.length ?? 0) > 0 || n.ref?.type === 'approval';
  const byTitle = new Map<string, CentreRow<N>>();
  for (const n of items) {
    const first = actionable(n) ? undefined : byTitle.get(n.title);
    if (first) first.more.push(n);
    else {
      const row = { n, more: [] as N[] };
      out.push(row);
      if (!actionable(n)) byTitle.set(n.title, row);
    }
  }
  return out;
}
